/**
 * 认证模块 - Token 管理
 * 负责 access_token、refresh_token 的存储、读取和清除
 */

export interface TokenInfo {
  access_token?: string;
  refresh_token?: string;
  expireTime: number;
}

/**
 * 设置会话 Token
 * @param access_token - 访问令牌
 * @param refresh_token - 刷新令牌
 * @param expireTime - 过期时间戳（毫秒）
 */
export function setSessionToken(
  access_token: string | undefined,
  refresh_token: string | undefined,
  expireTime: number,
): void {
  if (access_token) {
    localStorage.setItem('access_token', access_token);
  } else {
    localStorage.removeItem('access_token');
  }

  if (refresh_token) {
    localStorage.setItem('refresh_token', refresh_token);
  } else {
    localStorage.removeItem('refresh_token');
  }

  localStorage.setItem('expireTime', `${expireTime}`);
}

/**
 * 获取 Access Token
 * @returns Token 字符串，不存在则返回 null
 */
export function getAccessToken(): string | null {
  return localStorage.getItem('access_token');
}

/**
 * 获取 Refresh Token
 * @returns Token 字符串，不存在则返回 null
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem('refresh_token');
}

/**
 * 获取 Token 过期时间
 * @returns 时间戳字符串，不存在则返回 null
 */
export function getTokenExpireTime(): string | null {
  return localStorage.getItem('expireTime');
}

/**
 * 检查 Token 是否有效
 * @returns true - 有效，false - 已过期或不存在
 */
export function isTokenValid(): boolean {
  const expireTime = getTokenExpireTime();
  if (!expireTime) return false;

  const expireTimestamp = parseInt(expireTime);
  const now = Date.now();

  // 预留 30 秒缓冲时间
  return expireTimestamp > now + 30000;
}

/**
 * 检查是否需要刷新 Token（即将过期）
 * @returns true - 需要刷新，false - 不需要
 */
export function needRefreshToken(): boolean {
  const expireTime = getTokenExpireTime();
  if (!expireTime) return true;

  const expireTimestamp = parseInt(expireTime);
  const now = Date.now();

  // 如果 5 分钟内过期，需要刷新
  return expireTimestamp - now < 5 * 60 * 1000;
}

/**
 * 清除所有认证信息
 */
export function clearSessionToken(): void {
  sessionStorage.removeItem('user');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('expireTime');
}
